<template>
  <div class="share-job-container">
    <div class="dialog">
      <div class="dialog-title-container">
        <div class="title job-name">{{ shareTitle }}</div>
        <div v-if="!hiddenTitleTip" class="title tip-box">
          <span class="title label">{{ $t(`${i18nPrefix}.label.shareDialogTitle`) }}</span>
          <span class="tip-wrapper">
            <span class="icon-tip">?</span>
            <span class="title-tip" v-html="$t(`${i18nPrefix}.message.shareDialogTitleTip`)"></span>
          </span>
        </div>
      </div>
      <div class="dialog-content">
        <div v-if="!hiddenSourceCode" class="content-line source">
          <div class="line-label">
            {{ $t(`${i18nPrefix}.label.sourceName`) }}
            <span class="tip-wrapper">
              <span class="icon-tip">?</span>
              <span class="title-tip" v-html="$t(`${i18nPrefix}.message.sourceNameTip`)"></span>
            </span>
          </div>
          <div class="line-content source-input">
            <template v-if="canAddSourceCode">
              <el-autocomplete
                v-model="selectSource"
                clearable
                :fetch-suggestions="searchSourceList"
                :placeholder="$t(`admin.application.candidate.message.sourcePlaceholder`)"
              ></el-autocomplete>
            </template>
            <template v-else>
              <el-select
                v-model="selectSource"
                clearable
                filterable
                :placeholder="$t(`admin.application.candidate.message.sourcePlaceholder`)"
                :filter-method="handleFilterSource"
                @blur="validateSource"
                @clear="clearSource"
              >
                <el-option
                  v-for="(option, index) in staticSourceList"
                  :key="index"
                  :label="option.label"
                  :value="option['source'] || option.value"
                />
              </el-select>
            </template>
          </div>
        </div>
        <div v-if="!hiddenSourceCode" class="content-line code">
          <div class="line-label">
            {{ $t(`${i18nPrefix}.label.sourceDetail`) }}
            <span class="tip-wrapper">
              <span class="icon-tip">?</span>
              <span class="title-tip" v-html="$t(`${i18nPrefix}.message.sourceDetailTip`)"></span>
            </span>
          </div>
          <div class="line-content code-input">
            <template v-if="canAddSourceCode">
              <el-autocomplete
                v-model="selectCode"
                clearable
                :fetch-suggestions="searchCodeList"
                :placeholder="$t(`admin.application.candidate.message.codePlaceholder`)"
              ></el-autocomplete>
            </template>
            <template v-else>
              <el-select
                v-model="selectCode"
                clearable
                filterable
                :placeholder="$t(`admin.application.candidate.message.codePlaceholder`)"
                :filter-method="handleFilterCode"
                @blur="validateCode"
                @clear="clearCode"
              >
                <el-option
                  v-for="(option, index) in staticCodeList"
                  :key="index"
                  :label="option.label"
                  :value="option['code'] || option.value"
                />
              </el-select>
            </template>
          </div>
        </div>
        <div class="content-line link-result">
          <div class="line-label">URL</div>
          <div class="line-content">
            <el-input
              v-model="generateLink"
              disabled
              type="textarea"
              resize="none"
              :rows="2">
            </el-input>
          </div>
        </div>
      </div>
      <div class="dialog-footer">
        <div class="footer-btn btn-common btn-grey cancel" @click="handelCancel">
          {{ $t(`${i18nPrefix}.btn.shareDialog.cancel`) }}
        </div>
        <div class="btn-group">
          <div class="footer-btn btn-common btn-blue copy-link" @click="handelCopyURL">
            {{ $t(`${i18nPrefix}.btn.shareDialog.copyUrl`) }}
          </div>
          <div class="footer-btn btn-common btn-blue qr-code" @click="handelQRCode">
            {{ $t(`${i18nPrefix}.btn.shareDialog.createQRCode`) }}
          </div>
        </div>
      </div>
    </div>
    <DialogWrapper
      class="qr-dialog"
      appendToBody
      :show.sync="qrCodeDialog.show"
      :close-on-click-modal="false"
      :hideFooter="true"
    >
      <div class="qrcode-header">
        <div class="qrcode-header-close"></div>
        <div class="qrcode-header-title">{{ isJobShare ? jobName: companyName }}</div>
      </div>
      <div class="qrcode-wrapper">
        <img class="qrcode-body" :src="qrCodeUrl"/>
        <div class="qrcode-alt"></div>
      </div>
      <div class="qrcode-footer">
        <a :href="qrCodeUrl" class="download-box">
            <img src="/static/image/download.svg" />
            <span class="download-label">{{ $t(`${i18nPrefix}.btn.shareDialog.download`) }}</span>
        </a>
      </div>
      <div v-show="selectSource && selectCode" class="source-code-container">
        <div class="source-info-line">
          <label>{{ $t(`${i18nPrefix}.label.simpleSourceName`) }}</label>
          <span>{{ selectSource }}</span>
        </div>
        <div class="code-info-line">
          <label>{{ $t(`${i18nPrefix}.label.simpleCodeName`) }}</label>
          <span>{{ selectCode }}</span>
        </div>
      </div>
    </DialogWrapper>
    <RectLoading class="loading" v-show="loading" />
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import RectLoading from '@/components/loading/rectLoading'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'

export default {
  components: {
    RectLoading,
    DialogWrapper
  },
  props: {
    jobId: {
      type: Number
    },
    jobName: {
      type: String
    },
    companyId: {
      type: Number
    },
    companyName: {
      type: String
    },
    shareUrl: {
      type: String,
      required: true,
      default: ''
    },
    isJobShare: {
      type: Boolean,
      default: false
    },
    hiddenSourceCode: {
      type: Boolean,
      default: false
    },
    hiddenTitleTip: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.job.list',
      loading: false,
      companySourceCodeList: [],
      selectSource: null,
      isSearchSource: false,
      searchedSourceListByKeyword: [],
      sourceCodeList: [],
      selectCode: null,
      isSearchCode: false,
      searchedCodeListByKeyword: [],
      codeList: [],
      qrCodeUrl: '',
      canAddSourceCode: false,
      qrCodeDialog: {
        show: false
      }
    }
  },
  computed: {
    isEn () {
      return this.$i18n.locale === 'en'
    },
    generateLink () {
      let result = this.shareUrl
      if (this.selectSource && this.selectCode) {
        result += `?aj_source=${encodeURIComponent(this.selectSource)}&aj_code=${encodeURIComponent(this.selectCode)}`
      }
      return result
    },
    shareTitle () {
      if (this.isJobShare) {
        return `${this.jobName} ID${this.jobId}`
      }
      return this.companyName
    },
    staticSourceList () {
      if (this.canAddSourceCode) {
        return []
      }
      if (this.isSearchSource) {
        return _.map(this.searchedSourceListByKeyword, item => {
          return {
            label: item,
            value: item
          }
        })
      } else {
        const result = []
        _.forEach(this.companySourceCodeList, ({ source_list: sourceList }) => {
          _.forEach(sourceList, sourceInfo => {
            const source = sourceInfo.source
            if (!result.includes(source)) {
              result.push(source)
            }
          })
        })
        return _.map(result, item => {
          return {
            label: item,
            value: item
          }
        })
      }
    },
    staticCodeList () {
      if (this.canAddSourceCode) {
        return []
      }
      if (this.isSearchCode) {
        return _.map(this.searchedCodeListByKeyword, item => {
          return {
            label: item,
            value: item
          }
        })
      } else {
        const result = []
        const source = this.selectSource
        _.forEach(this.companySourceCodeList, ({ source_list: sourceList }) => {
          _.forEach(sourceList, (sourceInfo) => {
            if (source && sourceInfo.source !== source) {
              return
            }
            _.forEach(sourceInfo.code_list, codeInfo => {
              const code = codeInfo.code
              if (!result.includes(code)) {
                result.push(code)
              }
            })
          })
        })

        return _.map(result, item => {
          return {
            label: item,
            value: item
          }
        })
      }
    }
  },
  created () {
    this.initData()
  },
  methods: {
    ...mapActions('sourceCode', ['getCompanySourceCode']),
    async initData () {
      if (this.hiddenSourceCode) {
        return
      }
      this.loading = true
      try {
        const resp = await this.getCompanySourceCode()
        if (resp) {
          this.companySourceCodeList = resp.result
          this.canAddSourceCode = resp.can_add
        }
      } finally {
        this.loading = false
      }
    },
    searchSourceList (keywords, callback) {
      let result = []
      _.forEach(this.companySourceCodeList, ({ source_list: sourceList }) => {
        _.forEach(sourceList, sourceInfo => {
          const source = sourceInfo.source
          const searched = keywords ? (source.toUpperCase().includes(keywords.toUpperCase()) || sourceInfo.keyword?.toUpperCase().includes(keywords.toUpperCase())) : true
          if (!result.includes(source) && searched) {
            result.push(source)
          }
        })
      })
      result = _.sortBy(result)
      callback(_.map(result, val => { return { value: val } }))
    },
    handleFilterSource (keywords) {
      if (keywords) {
        this.selectSource = keywords
        this.searchSourceList(keywords, (result) => {
          this.searchedSourceListByKeyword = _.map(result, val => val.value)
        })
        this.isSearchSource = true
      } else {
        this.selectSource = null
        this.isSearchSource = false
      }
    },
    clearSource () {
      this.selectSource = null
      this.isSearchSource = false
    },
    validateSource () {
      const hasSource = _.includes(this.searchedSourceListByKeyword, this.selectSource)
      if (!this.canAddSourceCode && !hasSource && this.isSearchSource) {
        this.selectSource = null
        this.isSearchSource = false
      }
    },
    searchCodeList (keywords, callback) {
      let result = []
      const source = this.selectSource
      _.forEach(this.companySourceCodeList, ({ source_list: sourceList }) => {
        _.forEach(sourceList, (sourceInfo) => {
          if (source && sourceInfo.source !== source) {
            return
          }
          _.forEach(sourceInfo.code_list, codeInfo => {
            const code = codeInfo.code
            const searched = keywords ? (code.toUpperCase().includes(keywords.toUpperCase()) || codeInfo.keyword?.toUpperCase().includes(keywords.toUpperCase())) : true
            if (!result.includes(code) && searched) {
              result.push(code)
            }
          })
        })
      })
      result = _.sortBy(result)
      callback(_.map(result, val => { return { value: val } }))
    },
    handleFilterCode (keywords) {
      if (keywords) {
        this.selectCode = keywords
        this.searchCodeList(keywords, (result) => {
          this.searchedCodeListByKeyword = _.map(result, val => val.value)
        })
        this.isSearchCode = true
      } else {
        this.selectCode = null
        this.isSearchCode = false
      }
    },
    clearCode () {
      this.selectCode = null
      this.isSearchCode = false
    },
    validateCode () {
      const hasCode = _.includes(this.searchedCodeListByKeyword, this.selectCode)
      if (!this.canAddSourceCode && !hasCode && this.isSearchCode) {
        this.selectCode = null
        this.isSearchCode = false
      }
    },
    handelCancel () {
      this.$emit('close')
    },
    handelCopyURL () {
      window.prompt(this.$t(`${this.i18nPrefix}.message.copyURLTip`), this.generateLink)
    },
    handelQRCode () {
      this.qrCodeDialog.show = true
      if (this.isJobShare) {
        this.qrCodeUrl = `/django_rest/qr-code/?data=${encodeURIComponent(this.generateLink)}&job_id=${this.jobId}&att_name=${encodeURIComponent(this.jobName)}.png`
      } else {
        this.qrCodeUrl = `/django_rest/qr-code/?data=${encodeURIComponent(this.generateLink)}&company_id=${this.companyId}&att_name=${encodeURIComponent(this.companyName)}.png`
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.share-job-container {
  white-space: nowrap;

  .dialog-title-container {
    position: relative;
    .title {
      font-size: 14px;
      word-break: keep-all;

      .label {
        white-space: break-spaces;
      }
    }
    .job-name {
      font-size: 16px;
      font-weight: 700;
      white-space: break-spaces;
    }
    .tip-box {
      position: relative;
    }
  }
  .title-tip {
    display: none;
    position: absolute;
    margin-top: 8px;
    padding: 10px;
    color: #b5b5b5;
    background: #303133;
    border-radius: 8px;
    font-size: 12px;
    word-break: keep-all;
    white-space: break-spaces;
    z-index: 1;
    &:hover {
      display: block;
    }
  }
  .icon-tip {
    position: relative;
    display: inline-block;
    margin: 2px 0 0 6px;
    width: 14px;
    height: 14px;
    line-height: 14px;
    text-align: center;
    font-weight: 400;
    font-size: 14px;
    color: #aeaeae;
    border: 1px solid #aeaeae;
    border-radius: 50%;
    cursor: help;
    &::after {
      content: '';
      display: none;
      position: absolute;
      left: 1px;
      top: 12px;
      width: 0;
      height: 0;
      border: 6px solid #303133;
      border-left-color: transparent;
      border-right-color: transparent;
      border-top-color: transparent;
      z-index: 1;
    }
  }
  .tip-wrapper {
    &:hover {
      .icon-tip {
        &::after {
          display: block;
        }
      }
      .title-tip {
        display: block;
      }
    }
  }
  .dialog {
    .close-btn {
      width: 18px;
      height: 18px;
      background: url($baseDomain + 'static/image/icon_remove.png') no-repeat center;
      background-size: contain;
      cursor: pointer;
      position: absolute;
      top: 15px;
      right: 15px;
    }
  }
  .dialog-content {
    margin-top: 15px;
    position: relative;
    .content-line {
      margin-bottom: 15px;
      font-size: 14px;
      color: #333333;
      input {
        width: 100%;
        border: 1px solid #c0c4cc;
        box-sizing: border-box;
        cursor: text;
        height: 30px;
        line-height: 30px;
        padding: 2px 5px;
      }
      textarea {
        width: 100%;
        border: 1px solid #c0c4cc;
        box-sizing: border-box;
        cursor: text;
      }
      .line-label {
        position: relative;
        height: 22px;
        line-height: 22px;
        font-weight: 700;
        .title-tip {
          top: 20px;
          font-weight: 400;
          word-break: keep-all;
          white-space: break-spaces;

          ::v-deep {
            > a {
              color: #0096cc;
            }
          }
        }
      }
      .line-content {
        margin-top: 5px;
      }
    }
  }

  .dialog-footer {
    display: flex;
    margin-top: 20px;
    justify-content: space-between;

    .btn-common {
      color: #fff;
      display: inline-block;
      padding: 0 20px;
      height: 28px;
      line-height: 28px;
      margin: 0;
      font-weight: bold;
      text-align: center;
      white-space: nowrap;
      vertical-align: middle;
      cursor: pointer;
    }

    .btn-grey {
      background-color: #aaa;
    }

    .btn-blue {
      background-color: #0096cc;
    }

    .btn-group {
      .footer-btn {
        display: block;

        &:first-child {
          margin-bottom: 10px;
        }
      }
    }
  }

  .loading {
    position: absolute;
  }
}
</style>
<style lang="scss">
.qr-dialog.el-dialog__wrapper {
  .el-dialog {
    width: 380px;
    .el-dialog__body {
      padding-right: 40px;
    }
  }

  .qrcode-wrapper {
    .qrcode-body {
      width: 300px;
    }
  }

  .qrcode-footer {
    .download-box {
      background: #0096cc;
      margin: 0 auto 15px;
      height: 30px;
      width: 60%;
      display: flex;
      align-items: center;
      justify-content: center;

      img {
        width: 25px;
        vertical-align: middle;
      }
      .download-label {
        color: #FFF;
        font-size: 14px;
      }
    }
  }

  .source-code-container {
    border-top: 1px solid #c0c4cc;

    > div {
      padding: 5px 0 0;
    }

    label {
      font-weight: bold;
      margin-right: 5px;
    }
  }
}
</style>
